import { Dialog } from '@base-ui/react/dialog';
import { motion, useReducedMotion } from 'motion/react';
import type {
  CSSProperties,
  ComponentPropsWithoutRef,
  PointerEvent as ReactPointerEvent,
  ReactElement,
  ReactNode,
} from 'react';
import { createContext, forwardRef, useContext, useMemo, useState } from 'react';

type DrawerDirection = 'top' | 'bottom' | 'left' | 'right';

type DrawerRootProps = {
  activeSnapPoint?: number | string | null;
  autoFocus?: boolean;
  children?: ReactNode;
  closeThreshold?: number;
  container?: HTMLElement | null;
  defaultOpen?: boolean;
  defaultTriggerId?: string | null;
  direction?: DrawerDirection;
  disablePreventScroll?: boolean;
  dismissible?: boolean;
  fadeFromIndex?: number;
  fixed?: boolean;
  handleOnly?: boolean;
  modal?: boolean;
  nested?: boolean;
  noBodyStyles?: boolean;
  onDrag?: (event: ReactPointerEvent<HTMLDivElement>, percentageDragged: number) => void;
  onRelease?: (event: ReactPointerEvent<HTMLDivElement>, open: boolean) => void;
  onAnimationEnd?: (open: boolean) => void;
  onClose?: () => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  preventScrollRestoration?: boolean;
  repositionInputs?: boolean;
  scrollLockTimeout?: number;
  setBackgroundColorOnScale?: boolean;
  setActiveSnapPoint?: (snapPoint: number | string | null) => void;
  shouldScaleBackground?: boolean;
  snapPoints?: Array<number | string>;
  snapToSequentialPoint?: boolean;
  triggerId?: string | null;
};

type DrawerPortalProps = Omit<ComponentPropsWithoutRef<typeof Dialog.Portal>, 'keepMounted'>;

type DrawerOverlayProps = Omit<
  ComponentPropsWithoutRef<typeof Dialog.Backdrop>,
  'className' | 'style'
> & {
  className?: string;
  style?: CSSProperties;
};

type DrawerContentProps = Omit<ComponentPropsWithoutRef<typeof Dialog.Popup>, 'className' | 'style'> & {
  className?: string;
  style?: CSSProperties;
};

type DrawerTriggerProps = Omit<ComponentPropsWithoutRef<typeof Dialog.Trigger>, 'render'> & {
  asChild?: boolean;
  children?: ReactNode;
};

type DrawerCloseProps = Omit<ComponentPropsWithoutRef<typeof Dialog.Close>, 'render'> & {
  asChild?: boolean;
  children?: ReactNode;
};

type DrawerTitleProps = ComponentPropsWithoutRef<typeof Dialog.Title>;

type DrawerDescriptionProps = ComponentPropsWithoutRef<typeof Dialog.Description>;

type DrawerHandleProps = ComponentPropsWithoutRef<'div'> & {
  preventCycle?: boolean;
};

type DrawerContextValue = {
  container?: HTMLElement | null;
  direction: DrawerDirection;
  open: boolean;
};

const DRAWER_EASE: [number, number, number, number] = [0.215, 0.61, 0.355, 1];
const DRAWER_DURATION = 0.26;

const DrawerContext = createContext<DrawerContextValue | null>(null);

const useDrawerContext = () => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('Drawer components must be used within <Drawer.Root>.');
  }
  return context;
};

const getClosedTransform = (direction: DrawerDirection) => {
  switch (direction) {
    case 'left':
      return { x: '-100%', y: '0%' };
    case 'right':
      return { x: '100%', y: '0%' };
    case 'top':
      return { x: '0%', y: '-100%' };
    case 'bottom':
    default:
      return { x: '0%', y: '100%' };
  }
};

const resolveAsChildElement = (children: ReactNode): ReactElement | undefined => {
  if (!children) {
    return undefined;
  }

  if (Array.isArray(children)) {
    return children.length > 0 ? (children[0] as ReactElement) : undefined;
  }

  return children as ReactElement;
};

const Root = ({
  children,
  container,
  defaultOpen = false,
  direction = 'bottom',
  disablePreventScroll,
  dismissible = true,
  defaultTriggerId,
  modal = true,
  onAnimationEnd,
  onClose,
  onOpenChange,
  open: controlledOpen,
  triggerId,
}: DrawerRootProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = typeof controlledOpen === 'boolean';
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const resolvedModal =
    modal === false ? false : disablePreventScroll ? 'trap-focus' : (true as const);

  const contextValue = useMemo<DrawerContextValue>(
    () => ({
      open,
      direction,
      container,
    }),
    [container, direction, open],
  );

  return (
    <DrawerContext.Provider value={contextValue}>
      <Dialog.Root
        open={open}
        modal={resolvedModal}
        triggerId={triggerId}
        defaultTriggerId={defaultTriggerId}
        disablePointerDismissal={!dismissible}
        onOpenChange={(nextOpen, eventDetails) => {
          if (!dismissible && !nextOpen) {
            eventDetails.cancel();
            return;
          }

          if (!isControlled) {
            setUncontrolledOpen(nextOpen);
          }

          if (!nextOpen) {
            onClose?.();
          }

          onOpenChange?.(nextOpen);
        }}
        onOpenChangeComplete={nextOpen => {
          onAnimationEnd?.(nextOpen);
        }}
      >
        {children}
      </Dialog.Root>
    </DrawerContext.Provider>
  );
};

const NestedRoot = (props: DrawerRootProps) => <Root {...props} />;

const Portal = ({ children, container, ...props }: DrawerPortalProps) => {
  const context = useDrawerContext();
  return (
    <Dialog.Portal keepMounted container={container ?? context.container} {...props}>
      {children}
    </Dialog.Portal>
  );
};

const Overlay = forwardRef<HTMLDivElement, DrawerOverlayProps>(function Overlay(
  { className, style, ...props },
  ref,
) {
  const reduceMotion = useReducedMotion();
  const { open } = useDrawerContext();

  return (
    <Dialog.Backdrop
      ref={ref}
      render={
        <motion.div
          initial={false}
          animate={{ opacity: open ? 1 : 0 }}
          transition={reduceMotion ? { duration: 0 } : { duration: DRAWER_DURATION, ease: DRAWER_EASE }}
        />
      }
      data-state={open ? 'open' : 'closed'}
      data-vaul-overlay=""
      className={className}
      style={{
        pointerEvents: open ? 'auto' : 'none',
        ...style,
      }}
      {...props}
    />
  );
});

const Content = forwardRef<HTMLDivElement, DrawerContentProps>(function Content(
  { className, style, ...props },
  ref,
) {
  const reduceMotion = useReducedMotion();
  const { direction, open } = useDrawerContext();
  const closedTransform = getClosedTransform(direction);

  return (
    <Dialog.Popup
      ref={ref}
      render={
        <motion.div
          initial={false}
          animate={open ? { x: '0%', y: '0%' } : closedTransform}
          transition={reduceMotion ? { duration: 0 } : { duration: DRAWER_DURATION, ease: DRAWER_EASE }}
        />
      }
      data-state={open ? 'open' : 'closed'}
      data-vaul-animate={reduceMotion ? 'false' : 'true'}
      data-vaul-custom-container="true"
      data-vaul-drawer=""
      data-vaul-drawer-direction={direction}
      data-vaul-snap-points="false"
      className={className}
      style={style}
      {...props}
    />
  );
});

const Trigger = ({ asChild, children, ...props }: DrawerTriggerProps) => {
  if (asChild) {
    return <Dialog.Trigger render={resolveAsChildElement(children)} {...props} />;
  }

  return <Dialog.Trigger {...props}>{children}</Dialog.Trigger>;
};

const Close = ({ asChild, children, ...props }: DrawerCloseProps) => {
  if (asChild) {
    return <Dialog.Close render={resolveAsChildElement(children)} {...props} />;
  }

  return <Dialog.Close {...props}>{children}</Dialog.Close>;
};

const Title = forwardRef<HTMLHeadingElement, DrawerTitleProps>(function Title(props, ref) {
  return <Dialog.Title ref={ref} {...props} />;
});

const Description = forwardRef<HTMLParagraphElement, DrawerDescriptionProps>(function Description(
  props,
  ref,
) {
  return <Dialog.Description ref={ref} {...props} />;
});

const Handle = forwardRef<HTMLDivElement, DrawerHandleProps>(function Handle(
  { className, ...props },
  ref,
) {
  return <div ref={ref} data-vaul-handle="" className={className} {...props} />;
});

export const Drawer = {
  Root,
  NestedRoot,
  Content,
  Overlay,
  Trigger,
  Portal,
  Handle,
  Close,
  Title,
  Description,
};

export { Root, NestedRoot, Content, Overlay, Trigger, Portal, Handle, Close, Title, Description };

export type {
  DrawerCloseProps,
  DrawerContentProps,
  DrawerDescriptionProps,
  DrawerHandleProps,
  DrawerOverlayProps,
  DrawerPortalProps,
  DrawerRootProps,
  DrawerTitleProps,
  DrawerTriggerProps,
};
